import { message } from "antd";
import { CanceledError } from "axios";
import axiosInstance, { createAxiosInstance } from "./axios";
import { errorMessage } from "./errorMessageFunc";
const CONSOLE_LOG = false



// =============================  REQUEST CANCELLATION ====================================== :->

let axiosInstances = {};

const getInstance = (pathname, options={}) => {
  if (
    axiosInstances[pathname] &&
    !axiosInstances[pathname].controller.signal.aborted
  ) {
    return axiosInstances[pathname].instance;
  } else {
    const customAxiosInstance = createAxiosInstance(options=options);
    axiosInstances[pathname] = customAxiosInstance;
    return axiosInstances[pathname].instance;
  }
};

export const cancelPreviousPageRequests = (previousPath) => {
  const controller = axiosInstances?.[previousPath]?.controller;
  if (controller) {
      try {
          if (!controller?.signal?.aborted) {
              controller.abort();
              if (CONSOLE_LOG) console.error('Aborted previous request for path:', previousPath);
          } else {
            if (CONSOLE_LOG) console.error('Previous request already aborted:', previousPath);
          }
      } catch (error) {
          if (error instanceof CanceledError) {
            if (CONSOLE_LOG) console.error('Request already canceled:', error.message);
          } else {
            if (CONSOLE_LOG) console.error('Encountered error while aborting request: ', error);
          }
      }
  } else {
      console.error('No controller found for path:', previousPath);
  }
  delete axiosInstances[previousPath];
};

export const makeBackendRequest = (method, url, data = null) => {
  const instance = getInstance(window.location.pathname);
  return instance[method](url, data);
};


// =============================================================================================== :->


function displayMessage(response) {
  if (response === undefined) return
  if (response.status === 200 || response.status === 201) {
    message.success(response?.data?.description);
  } else {
    if (CONSOLE_LOG) {
      console.error("error", response);
    };
    let errMsg = response?.data?.description || "";
    if (errMsg === undefined) {
      errMsg = response?.data?.detail || "";
    }
    errorMessage(errMsg)
  }
}

function consoleLog(method, backendURL, type, data) {
  if (CONSOLE_LOG) {
    console.log(method + " " + backendURL + " " + type, data);
  };
}

function defaultThen(method, backendURL, response, ignoreReload = false) {
  consoleLog(method, backendURL, "response", response.data);
  if (response.status === 200) {
    displayMessage(response);
    if (ignoreReload) {
      return;
    }
    setTimeout(() => {
      window.location.reload(false);
    }, 2000);
  }
}

export function defaultCatch(method, backendURL, response) {
  if (response === undefined) return
  displayMessage(response);
  consoleLog(method, backendURL, "error", response.data);
  if (response.status === 401) {
    errorMessage("Unauthorized or expired credentials. Logging out...", 0);
    window.location.href = "/logout";
  }
}

export function backendGet(
  self,
  backendURL,
  data,
  loadingKey,
  updateStateFunc // takes in state and response data
) {
  consoleLog("GET", backendURL, "params", data);
  const instance = getInstance(window.location.pathname);

  instance
    .get(backendURL, data)
    .then((response) => {
      let newState = self.state;
      consoleLog("GET", backendURL, "response", response.data);
      newState = updateStateFunc(newState, response.data);
      newState[loadingKey] = false;
      self.setState(newState);
    })
    .catch((error) => {
      defaultCatch("GET", backendURL, error.response);
    });
}

export function backendGetHTML(
  backendURL,
  data,
) {
  consoleLog("GET", backendURL, "params", data);
  const instance = getInstance(window.location.pathname);

  instance
    .get(backendURL, data)
    .then((response) => {
      let newWin = window.open("about:blank", "res.data", "width=400,height=400");
      newWin.document.write(response.data)
    })
    .catch((error) => {
      defaultCatch("GET", backendURL, error.response);
    });
}

export function backendGetPDF(
  backendURL,
  data,
) {
  consoleLog("GET", backendURL, "params", data);

  const instance = getInstance(window.location.pathname);

  instance
    .get(backendURL, data)
    .then((response) => {
      //Create a Blob from the PDF Stream
      const file = new Blob([response.data], { type: "application/pdf" });
      //Build a URL from the file
      const fileURL = URL.createObjectURL(file);
      //Open the URL on new Window
      const pdfWindow = window.open();
      pdfWindow.location.href = fileURL;
    })
    .catch((error) => {
      defaultCatch("GET", backendURL, error.response);
    });
}

export function backendPost(backendURL, data, ignoreThen = false, ignoreReload = false) {
  message.loading("Submitting request..", 2);
  consoleLog("POST", backendURL, "params", data);

  const instance = getInstance(window.location.pathname);

  instance
    .post(backendURL, data, {})
    .then((response) => {
      if (ignoreThen) {
        consoleLog("POST", backendURL, "response", response.data);
        return response.data;
      } else {
        defaultThen("POST", backendURL, response, ignoreReload);
      }
    })
    .catch((error) => {
      defaultCatch("POST", backendURL, error.response);
    });
}

export function backendPatch(backendURL, data) {
  message.loading("Submitting request..", 2);
  consoleLog("PATCH", backendURL, "params", data);

  const instance = getInstance(window.location.pathname);

  instance
    .patch(backendURL, data, {})
    .then((response) => {
      defaultThen("PATCH", backendURL, response);
    })
    .catch((error) => {
      defaultCatch("PATCH", backendURL, error.response);
    });
}

export function backendDelete(backendURL, data) {
  message.loading("Submitting request..", 2);
  consoleLog("DELETE", backendURL, "params", data);

  const instance = getInstance(window.location.pathname);

  instance
    .delete(backendURL, { data: data })
    .then((response) => {
      defaultThen("DELETE", backendURL, response);
    })
    .catch((error) => {
      defaultCatch("DELETE", backendURL, error.response);
    });
}

// =============================  WITHOUT REFRESH ====================================== :->


export function backendPostWithoutRefresh(backendURL, data, ignoreThen = false, showMessage = true) {
  if (showMessage) {
    message.loading("Submitting request..", 2);
  }
  consoleLog("POST", backendURL, "params", data);

  const instance = getInstance(window.location.pathname);

  return instance
    .post(backendURL, data, {})
    .then((response) => {
      if (ignoreThen) {
        consoleLog("POST", backendURL, "response", response.data);
      } else {
        if (showMessage) {
          displayMessage(response);
        }
        return response;
      }
    })
    .catch((error) => {
      defaultCatch("POST", backendURL, error.response);
      const bubbledError = new Error(error)
      bubbledError.status = error.status
      throw bubbledError
    });
}


export function backendDeleteWithoutRefresh(backendURL, data) {
  message.loading("Submitting request..", 2);
  consoleLog("DELETE", backendURL, "params", data);

  const instance = getInstance(window.location.pathname);

  return instance
    .delete(backendURL, { data: data })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      defaultCatch("DELETE", backendURL, error.response);
    });
}


export function backendGetRequest(
  backendURL,
  data,
  handleCatch=true,
  options={}
) {
  consoleLog("GET", backendURL, "params", data);

  const instance = getInstance(window.location.pathname, options=options);

  return instance
    .get(backendURL, data)
    .then((response) => {
      return response
    })
    .catch((error) => {
      if (handleCatch) {
        defaultCatch("GET", backendURL, error.response);
      }
      return error;
    });
}

export function backendPatchWithoutRefresh(backendURL, data, showMessage=true) {
  if (showMessage) {
    message.loading("Submitting request..", 2);
  }
  consoleLog("PATCH", backendURL, "params", data);

  const instance = getInstance(window.location.pathname);

  return instance
    .patch(backendURL, data, {
      validateStatus: (status) => {
        return status < 400;
      }
    })
    .then((response) => {
      return response
    })
    .catch((error) => {
      defaultCatch("PATCH", backendURL, error.response);
      return error
    });
}

export function backendPutWithoutRefresh(backendURL, data) {
  message.loading("Submitting request..", 2);
  consoleLog("PUT", backendURL, "params", data);

  const instance = getInstance(window.location.pathname);

  return instance
    .put(backendURL, data, {})
    .then((response) => {
      return response;
    })
    .catch((error) => {
      defaultCatch("PATCH", backendURL, error.response);
    });
}

export function backendPostReturnPromise(backendURL, data) {
  consoleLog("POST", backendURL, "params", data);

  const instance = getInstance(window.location.pathname);

  return instance
    .post(backendURL, data, {});
}


// =============================  WITH REDIRECT ====================================== :->


export function backendPostWithRedirect(backendURL, data, redirectURL) {
  message.loading("Submitting request..", 2);
  consoleLog("POST", backendURL, "params", data);
  return axiosInstance
    .post(backendURL, data, {})
    .then((response) => {
      window.location.href = redirectURL;
    })
    .catch((error) => {
      defaultCatch("POST", backendURL, error.response);
      return error;
    });
}


// ============================= CACHING ====================================== :->


function urlWithParams(backendURL, data) {
  const params = new URLSearchParams(data?.params);
  return `${backendURL}?${params.toString()}`;
}

function getCacheEndpoint(backendURL) {
  return `/api/cache/${backendURL.replace(/^(\/api\/|\/api\/)/, '')}`;
}

export function backendGetCached(backendURL, data) {
  const cacheEndpoint = getCacheEndpoint(backendURL);
  return backendGetRequest(cacheEndpoint, data, false);
}

export function backendUpdateCache(backendURL, params, data) {
  const cacheEndpoint = getCacheEndpoint(backendURL);
  const cacheEndpointWithParams = urlWithParams(cacheEndpoint, params);
  const instance = getInstance(window.location.pathname);
  instance.post(cacheEndpointWithParams, data, {});
}

export function backendGetRequestWithCache(backendURL, data, onCachedResponse, onResponse, onError=null) {
  consoleLog("GET", backendURL, "params", data);
 
  const instance = getInstance(window.location.pathname);
  const cacheEndpoint = getCacheEndpoint(backendURL);
  const cachedPromise = instance.get(cacheEndpoint, data);
  const nonCachedPromise = instance.get(backendURL, data);
 
  cachedPromise.then((cachedResponse) => {
    onCachedResponse(cachedResponse);
  }).catch((error) => {
    defaultCatch("GET", backendURL, error.response);
  });

  nonCachedPromise.then((nonCachedResponse) => {
    // update cache with latest response
    const cacheEndpointWithParams = urlWithParams(cacheEndpoint, data);
    instance.post(cacheEndpointWithParams, nonCachedResponse?.data, {});
    onResponse(nonCachedResponse);
  }).catch((error) => {
    if (onError) onError();
  });
 }
